import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { Check, X, Clock, ShieldAlert, Receipt } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TIMELINE = ["requested", "approved", "picked_up", "inspected", "refunded"];

const ReturnDetail = () => {
    const { id } = useParams();
    const [ret, setRet] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = () => api.get(`/returns/${id}`).then((r) => setRet(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, [id]);

    const cancel = async () => {
        if (!window.confirm("Cancel this return?")) return;
        try {
            await api.post(`/returns/${id}/cancel`);
            toast.success("Return cancelled");
            fetchData();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not cancel");
        }
    };

    if (loading || !ret) {
        return (
            <AuthGuard>
                <Navbar />
                <main className="mx-auto max-w-3xl p-10 text-sm text-zinc-500">Loading…</main>
            </AuthGuard>
        );
    }

    const statusIndex = TIMELINE.indexOf(ret.status);
    const isRejected = ret.status === "rejected" || ret.status === "cancelled";

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
                    <Link to="/returns" className="text-xs text-zinc-500 hover:underline">← All returns</Link>
                    <div className="mt-2 flex items-end justify-between gap-3">
                        <div>
                            <p className="label-uppercase text-zinc-500">Return</p>
                            <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>
                                Order {ret.order_no || ret.order_id?.slice(0, 8)}
                            </h1>
                            <p className="mt-1 text-sm text-zinc-500">Requested {new Date(ret.created_at).toLocaleString()}</p>
                        </div>
                        {["requested", "approved"].includes(ret.status) && (
                            <button onClick={cancel} className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50" data-testid="return-cancel-btn">
                                Cancel return
                            </button>
                        )}
                    </div>

                    {ret.ai_validation?.flagged && (
                        <p className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800" data-testid="return-flagged-banner">
                            <ShieldAlert className="h-3.5 w-3.5" /> Flagged for manual review — our team will follow up shortly.
                        </p>
                    )}

                    {/* Timeline */}
                    <ol className="mt-8 grid grid-cols-5 gap-2 text-center text-xs" data-testid="return-timeline">
                        {TIMELINE.map((step, i) => {
                            const reached = !isRejected && i <= statusIndex;
                            const isCurrent = i === statusIndex;
                            return (
                                <li key={step} className="flex flex-col items-center">
                                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-medium ${
                                        reached ? "bg-brand-700 text-white" : "bg-zinc-100 text-zinc-500"
                                    } ${isCurrent ? "ring-2 ring-brand-200" : ""}`}>
                                        {reached ? <Check className="h-3.5 w-3.5" /> : i + 1}
                                    </span>
                                    <span className={`mt-1 text-[10px] uppercase tracking-wide ${reached ? "text-zinc-700" : "text-zinc-400"}`}>{step.replace("_", " ")}</span>
                                </li>
                            );
                        })}
                    </ol>

                    {isRejected && (
                        <div className="mt-6 flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm text-rose-800">
                            <X className="mt-0.5 h-4 w-4" />
                            <div>
                                <p className="font-medium">Return {ret.status}</p>
                                {ret.inspector_notes && <p className="mt-1 text-rose-700">{ret.inspector_notes}</p>}
                            </div>
                        </div>
                    )}

                    {/* Items */}
                    <section className="mt-8">
                        <p className="label-uppercase text-zinc-500">Items</p>
                        <ul className="mt-3 space-y-3">
                            {(ret.items || []).map((it, i) => (
                                <li key={i} className="flex items-start gap-4 rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03]">
                                    <div className="flex-1">
                                        <p className="font-medium text-zinc-900 dark:text-zinc-50">{it.product_name}</p>
                                        <p className="mt-0.5 text-xs text-zinc-500">Qty {it.qty_returned} • {inr(it.unit_price)} each • {it.reason.replace("_", " ")}</p>
                                        {it.notes && <p className="mt-2 text-xs text-zinc-600">{it.notes}</p>}
                                    </div>
                                    <div className="text-right text-xs">
                                        <p className="text-zinc-500">Refund</p>
                                        <p className="font-medium text-zinc-900 dark:text-zinc-50">{inr(it.qty_returned * it.unit_price)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* Photos */}
                    {ret.items?.some((it) => (it.photos || []).length) && (
                        <section className="mt-8">
                            <p className="label-uppercase text-zinc-500">Photos</p>
                            <div className="mt-3 flex flex-wrap gap-3">
                                {ret.items.flatMap((it) => it.photos || []).map((p) => (
                                    <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer" className="block h-24 w-24 overflow-hidden rounded-lg border border-zinc-200">
                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="h-full w-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Refund */}
                    {ret.refund && (
                        <section className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5">
                            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><Receipt className="h-4 w-4" /> Refund {ret.refund.status}</p>
                            <p className="mt-2 text-sm text-emerald-700">
                                {inr(ret.refund.amount)} via {ret.refund.method?.replace("_", " ") || "—"}
                                {ret.refund.zoho_refund_id && <span className="ml-2 text-xs text-emerald-600">({ret.refund.zoho_refund_id})</span>}
                            </p>
                        </section>
                    )}

                    {/* History */}
                    <section className="mt-8">
                        <p className="label-uppercase text-zinc-500">Activity</p>
                        <ol className="mt-3 space-y-2">
                            {(ret.history || []).slice().reverse().map((h, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs">
                                    <Clock className="mt-0.5 h-3 w-3 text-zinc-400" />
                                    <span className="text-zinc-500">{new Date(h.at).toLocaleString()}</span>
                                    <span className="text-zinc-700 dark:text-zinc-300">{h.event.replace("_", " ")}</span>
                                    {h.note && <span className="text-zinc-500">— {h.note}</span>}
                                </li>
                            ))}
                        </ol>
                    </section>
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default ReturnDetail;
